import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Searchbar } from '@atlassian/learning-components';
import { debounce } from 'lodash';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';

import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';

interface SearchbarProps {
  onSearchInteraction: () => void;
  isLoadingResources: boolean;
}
export const SearchbarWrapper: FC<SearchbarProps> = ({ onSearchInteraction, isLoadingResources }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState('');
  const { user } = useAtlassianUser();

  useEffect(() => {
    const searchValue = searchParams.get('search') || '';
    const sanitizedValue = searchValue.replaceAll(/[&\\#,+()$~%.'":*?<>{}]/g, '');

    setValue(sanitizedValue);
    updateSearchParams(sanitizedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchParams.get('search')) {
      setValue('');
    }
  }, [searchParams]);

  const updateSearchParams = useCallback(
    (value: string) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (!value.length) {
        newSearchParams.delete('search');
      } else {
        newSearchParams.set('search', value);
      }

      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const debouncedRequest = useMemo(() => debounce(updateSearchParams, 1000), [updateSearchParams]);

  const onChange = (value: string): void => {
    const isEmptySearch = !value.trim().length;

    if (!/^[a-zA-Z0-9 \s]+$/.test(value) && !isEmptySearch) {
      return;
    }

    if (isEmptySearch) {
      setValue('');
    } else {
      setValue(value);

      sendUIEvent(
        'catalogScreen',
        'search',
        'clicked',
        'catalogSearch',
        {
          value,
        },
        user?.account_id,
      );
    }

    onSearchInteraction();
    debouncedRequest(value);
  };

  return (
    <div css={searchbarWrapperStyles}>
      <Searchbar onChange={onChange} value={value} isLoading={isLoadingResources} />
    </div>
  );
};

const searchbarWrapperStyles = css({
  [media.above.md]: {
    width: '100%',
  },

  [media.below.md]: {
    width: '275px',
  },

  [media.below.xs]: {
    flex: 1,
  },
});
